// Hero Section Component
// ======================
// Feature #287: Updated with refined design - subtle accents, elegant typography

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import DesignLine from '../ui/DesignLine';
import AnnaGlow from '../ui/AnnaGlow';

const HeroSection: React.FC = () => {
  const { t } = useTranslation('landing');

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-[80vh] flex items-center justify-center bg-background-light dark:bg-background-dark pt-20">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-96 bg-gradient-to-b from-primary-50/50 to-transparent dark:from-primary-900/20 dark:to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-white/50 to-transparent dark:from-background-dark dark:to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="text-center">
          {/* AnnaGlow orb — organic visual anchor */}
          <div className="flex justify-center mb-6 animate-fade-in-up" style={{ animationDelay: '0s', animationFillMode: 'both' }}>
            <AnnaGlow size="lg" />
          </div>

          {/* Badge */}
          <div className="inline-flex items-center px-5 py-2.5 bg-canvas dark:bg-canvas-dark border border-frame-border dark:border-frame-border-dark rounded-full text-sm mb-8 shadow-sm animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
            <span className="mr-2">🎓</span>
            <span className="text-neutral-dark dark:text-gray-200 font-medium">
              {t('hero.badge')}
            </span>
          </div>

          {/* Main heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold text-neutral-dark dark:text-white mb-6 leading-tight animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
            {t('hero.titleLine1')}
            <br />
            <span className="bg-gradient-to-r from-design-line-blue via-primary-500 to-design-line-orange bg-clip-text text-transparent">
              {t('hero.titleLine2')}
            </span>
          </h1>

          {/* Design-line accent */}
          <div className="flex justify-center mb-8 animate-fade-in-up" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
            <DesignLine variant="horizontal" width="120px" height={2} />
          </div>

          {/* Subheading */}
          <p className="text-lg sm:text-xl text-neutral-medium dark:text-gray-400 max-w-2xl mx-auto mb-10 font-sans leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.4s', animationFillMode: 'both' }}>
            {t('hero.subtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in-up" style={{ animationDelay: '0.5s', animationFillMode: 'both' }}>
            <Link to="/register">
              <Button
                variant="primary"
                size="lg"
                className="shadow-md hover:shadow-lg transition-shadow"
              >
                {t('hero.ctaPrimary')}
              </Button>
            </Link>
            <Button
              variant="outline"
              size="lg"
              onClick={() => scrollToSection('how-it-works')}
            >
              {t('hero.ctaSecondary')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
